import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, User, Tag, ArrowLeft, Clock,
  Share2, Facebook, Twitter, Linkedin, Copy, Check, BookOpen,
} from 'lucide-react';
import SEO from '@/components/common/SEO';
import { useToast } from '@/contexts/ToastContext';
import { getPostBySlug, posts } from '@/data/posts';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const articleRef = useRef(null);

  const post = getPostBySlug(slug);

  useEffect(() => {
    const onScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const { top, height } = el.getBoundingClientRect();
      const visible = Math.min(Math.max(-top / (height - window.innerHeight), 0), 1);
      setReadProgress(Math.round(visible * 100));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [post]);

  const readTime = (content = '') =>
    Math.max(1, Math.ceil(content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200));

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const sharePost = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast?.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const relatedPosts = posts.filter(
    (p) => p.id !== post?.id && p.category?.slug === post?.category?.slug
  ).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#080d14] text-white pt-20 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto">
            <BookOpen size={36} className="text-gray-600" />
          </div>
          <h1 className="text-4xl font-bold">Post Not Found</h1>
          <p className="text-gray-400">This article doesn't exist or may have been moved.</p>
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-semibold transition-colors"
          >
            <ArrowLeft size={18} /> Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080d14] text-white pt-20">
      <SEO
        title={post.title}
        description={post.excerpt || post.content.substring(0, 160).replace(/<[^>]*>/g, '')}
        image={post.featured_image}
        url={`/blog/${post.slug}`}
        type="article"
        author={post.author?.name}
        publishedTime={post.published_at || post.created_at}
        tags={post.tags || []}
      />

      {/* Read progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-white/5">
        <motion.div
          className="h-full bg-emerald-500"
          style={{ width: `${readProgress}%` }}
          transition={{ ease: 'linear' }}
        />
      </div>

      <div className="max-w-3xl mx-auto px-4">
        {/* Back */}
        <div className="py-8">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-400 text-sm font-medium transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Blog
          </button>
        </div>

        <article ref={articleRef}>
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 space-y-6"
          >
            {post.category && (
              <Link
                to={`/blog?category=${post.category.slug}`}
                className="inline-block px-4 py-1.5 bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-wide hover:bg-emerald-500/25 transition-colors"
              >
                {post.category.name}
              </Link>
            )}

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-white">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500 border-b border-white/6 pb-6">
              {post.author?.name && (
                <span className="flex items-center gap-1.5">
                  <User size={14} /> {post.author.name}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                {formatDate(post.published_at || post.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} /> {readTime(post.content)} min read
              </span>
            </div>

            {/* Share */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 uppercase tracking-widest font-semibold mr-1">Share</span>
              {[
                { platform: 'facebook', icon: Facebook, color: 'hover:bg-blue-600/20 hover:text-blue-400 hover:border-blue-500/30' },
                { platform: 'twitter', icon: Twitter, color: 'hover:bg-sky-500/20 hover:text-sky-400 hover:border-sky-500/30' },
                { platform: 'linkedin', icon: Linkedin, color: 'hover:bg-blue-700/20 hover:text-blue-400 hover:border-blue-700/30' },
              ].map(({ platform, icon: Icon, color }) => (
                <button
                  key={platform}
                  onClick={() => sharePost(platform)}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl border border-white/8 bg-white/4 text-gray-500 transition-all ${color}`}
                  aria-label={`Share on ${platform}`}
                >
                  <Icon size={15} />
                </button>
              ))}
              <button
                onClick={copyLink}
                className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all ${
                  copied
                    ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400'
                    : 'border-white/8 bg-white/4 text-gray-500 hover:bg-emerald-500/15 hover:text-emerald-400 hover:border-emerald-500/30'
                }`}
                aria-label="Copy link"
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </div>
          </motion.header>

          {post.featured_image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl overflow-hidden mb-12 border border-white/6 shadow-2xl"
            >
              <img src={post.featured_image} alt={post.title} className="w-full h-auto" />
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg prose-invert max-w-none mb-14
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
              prose-p:text-gray-300 prose-p:leading-relaxed
              prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white prose-code:text-emerald-300
              prose-code:bg-white/6 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-sm
              prose-pre:bg-[#0f1923] prose-pre:border prose-pre:border-white/8 prose-pre:rounded-xl
              prose-blockquote:border-l-emerald-500 prose-blockquote:text-gray-400
              prose-img:rounded-xl prose-img:border prose-img:border-white/8"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mb-12">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?search=${tag}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/8 hover:border-emerald-500/30 hover:text-emerald-400 rounded-lg text-gray-400 text-sm transition-colors"
                  >
                    <Tag size={12} /> {tag}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Bottom share strip */}
          <div className="border-t border-white/6 pt-10 pb-20">
            <p className="text-center text-gray-500 text-sm mb-5">Found this helpful? Share it.</p>
            <div className="flex justify-center gap-3">
              {[
                { platform: 'facebook', icon: Facebook, label: 'Facebook' },
                { platform: 'twitter', icon: Twitter, label: 'Twitter' },
                { platform: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
              ].map(({ platform, icon: Icon, label }) => (
                <button
                  key={platform}
                  onClick={() => sharePost(platform)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/8 text-gray-400 hover:text-white hover:bg-white/10 text-sm font-medium transition-all"
                >
                  <Icon size={15} /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Related posts */}
          {relatedPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="border-t border-white/6 pt-14 pb-20"
            >
              <h3 className="text-2xl font-bold mb-8">Related Articles</h3>
              <div className="grid sm:grid-cols-3 gap-5">
                {relatedPosts.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/blog/${rp.slug}`}
                    className="group bg-white/4 border border-white/6 hover:border-emerald-500/25 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5"
                  >
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-white line-clamp-2 group-hover:text-emerald-400 transition-colors mb-1.5">
                        {rp.title}
                      </h4>
                      <p className="text-xs text-gray-500 line-clamp-2">{rp.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
